import Mustache from 'mustache';
import nextUntil from './nextUntil';

const multiselects = {};

/**
 * Multiselect refactored
 * Supports single select and optgroups in multiselect
 *
 * Available public methods
 * addCallback(Object),
 * addOptions(Array),
 * removeOptions(Array),
 * unSelectAll(Boolean),
 * openSelect(),
 * closeSelect(),
 * anyOptionsSelected(),
 * getValue(),
 * disableMultiselect(),
 * ensableMultiselect()
 *
 * @example HTML
 * // Create select element to document, and add remultiselect class amd unique ID to it
 * <select class="remultiselect" id="make" multiple>
 *  // You can add optgroup with labels or just add options
 *  <optgroup label="Suosituimmat autot">
 *    <option value="Audi">Audi</option>
 *    <option value="Saab">Saab</option>
 *  </optgroup>
 *  <optgroup label="Kaikki autot">
 *    <option value="Skoda">Skoda</option>
 *    <option value="Lada">Lada</option>
 * </select>
 *
 * @example JavaScript: Add callback to created multiselect
 * // Available callbacks: onValueChange, onMenuOpen, onMenuClose
 * window.multieslects.make.addCallback({
 *  onValueChange : function(multiselectData, wasUnSelectAll) {
 *    console.log(multiselectData.options);
 *    // All functions available in multiselectData
 *    // I.E.:
 *    const multiselectCurrentValue = multiselectData.getValue();
 *  },
 *  onMenuOpen : function(multiselectData) {
 *    console.log('tralalaa');
 *  },
 *  onMenuClose : function(multiselectData) {
 *    console.log('closed');
 *  }
 * });
 *
 * @example JavaScript: Adding values to make select
 * // onValueChange function will fire if some of new values has selected
 * window.multiselects.make.addOptions([
 *  { value : 'volkswagen', shownValue : 'Folkkari', selected : true },
 *  { value : 'bmw',        shownValue : 'Biimeri', datasets : [{ datasetName : 'foo', datasetValue : 'bar' }] }
 * ]);
 *
 * @example JavaScript: Remove option(s) from multiselect
 * // onValueChange function will fire if some of new values has selected
 * window.multiselects.make.removeOption(['volkswagen', 'bmw']);
 *
 * @example JavaScript: Unselect all options in multiselect
 * window.multiselects.make.unSelectAll();
 *
 */
class ReMultiselect {
  // Base template of multiselect
  template = `<button type="button" class="multiselect-open{{#selectDetails.isDisabled}} disabled{{/selectDetails.isDisabled}}"><div class="multiselect-open__caption">{{#caption}}{{caption}}{{/caption}}{{^caption}}{{defaultCaption}}{{/caption}}</div></button>
  <div class="list-container">
    <button style="display: {{# showClearAll}}block{{/ showClearAll}}{{^ showClearAll}}none{{/ showClearAll}};" class="clear-selection clear-selection_button"><span class="clear-selection__text"></span>&nbsp;<span class="clear-selection__count">n</span></button>
    <ul class="{{#selectDetails.isMultiple}}multiple{{/selectDetails.isMultiple}}"
        data-select-name="{{selectDetails.name}}"
        {{#selectDetails.datasets}}data-{{datasetName}}="{{datasetValue}}"{{/selectDetails.datasets}}>
        {{>optionsList}}
    </ul>
  </div>`;

  // Partial can be used when adding new options
  optionsPartial = `{{#options}}
    {{#isCaption}}
      <li class="separator" data-separator-label="{{shownValue}}" data-separator="true"{{#datasets}} data-{{{datasetName}}}="{{datasetValue}}"{{/datasets}}>{{shownValue}}</li>
    {{/isCaption}}
    {{^isCaption}}
      <li class="{{#selected}}selected{{/selected}}" data-value="{{{value}}}"{{#datasets}} data-{{{datasetName}}}="{{datasetValue}}"{{/datasets}}{{#hidden}} hidden{{/hidden}}>
        {{shownValue}}
      </li>
    {{/isCaption}}
  {{/options}}`;

  // Used when adding new options
  originalOptionsTemplate = `{{#options}}
    <option value="{{{value}}}"{{#selected}} selected{{/selected}}{{#datasets}} data-{{{datasetName}}}="{{datasetValue}}"{{/datasets}}>{{shownValue}}</option>
  {{/options}}`;

  /**
   * Creates multiselect.
   *
   * @param {object} element - Select element
   * @param {object} callbacks    - Callbacks to bind in object
   */
  constructor(element, callbacks) {
    if (typeof window.multiselects === 'undefined') {
      window.multiselects = {};
      window.activeMultiselectId = null;
    }

    // Current value of element, returned in this.getValue()
    this.value = [];
    // Original select element
    this.element = element;

    if (typeof this.element.id === 'undefined' || this.element.id === '') {
      console.warn('Multiselect original select element must have id defined!');
      return false;
    }

    if (window.multiselects[this.element.id]) {
      console.warn('There is multiselect with id: ' + this.element.id + ' in dom! Could not proceed.');
      return false;
    }

    // Callbacks includes for example onValueChange evets etc.
    this.callbacks = [];

    // selectOpened defines if current multiselect list is opened
    this.selectOpened = false;

    if (typeof callbacks === 'object' && Object.keys(callbacks).length > 0) {
      this.addCallback(callbacks);
    }

    // Select wrapper is used to point events etc.
    this.selectWrapper = null;

    // Get current options from select element
    this.selectDetails = this._collectSelectDetails();
    this.options = this._optionsToJSON();
    this._renderUI();
    window.multiselects[this.element.id] = this;

    // If select is multiple, and options has first option with empty value
    // unselect all, because first is label only
    if (
      !this.selectDetails.isMultiple &&
      this.options.some(function (option) {
        return option.value === '' && option.selected;
      })
    ) {
      this.unSelectAll(true);
    }

    // Update value if preselected options
    this._setValue();
    this._updateSelectButtonCaption();

    // update clear all text
    this._updateClearAll();
  }

  /**
   * Push new callback function to this.callbacks
   *
   * @param {object} callbackFunctions - Callback function in object where key is type of function and content is function itself
   *
   * @returns {undefined}
   * @example
   * window.makeSelect.addCallback({
   *  onValueChange : function(event) {
   *    // Get models from ajax
   *    // Do sth
   *    window.modelSelect.updateOptions(ajaxResponse);
   *  },
   *  onMenuOpen  : function(event) {},
   *  onMenuClose : function(event) {}
   *  ...
   * })
   */
  addCallback(callbackFunctions) {
    for (const [functionName, functionContent] of Object.entries(callbackFunctions)) {
      if (typeof functionContent !== 'function') {
        console.warn('Function ' + functionName + ' is not a function!');
      } else {
        this.callbacks[functionName] = functionContent;
      }
    }
  }

  /**
   * Add new options to select
   * @param {array}  newOptions    - New options in object, selected parameter is not mandatory (default: false). Datasets in array.
   * @param {string} underOptGroup - If select menu uses optgroups, you can define which optgroup option is inserted to. If null, option to the bottom of the list
   *
   * @returns {undefined}
   *
   * @example
   * constructedMultiselect.addOptions([
   *   { value : 'foo', shownValue : 'Foo', selected : true },
   *   { value : 'bar', shownValue : 'Bar', datasets: [{ datasetName : 'foobar', datasetValue : 'barfoo' }] }
   *  ]);
   *
   * // Datasets for value "bar" will be printed data-foobar="barfoo"
   */
  addOptions(newOptions, underOptGroup = null) {
    if (typeof newOptions !== 'object' || !Array.isArray(newOptions)) {
      console.error('Could not add new options. newOptions is not an array!');
      return;
    }

    // Test duplicate values
    const duplicatesCheck = [];
    newOptions.forEach(function (options) {
      duplicatesCheck.push(options.value);
    });

    // Current options
    this.options.forEach(function (options) {
      duplicatesCheck.push(options.value);
    });

    if (duplicatesCheck.length !== new Set(duplicatesCheck).size) {
      console.warn('Duplicate values detected! Could not add options to multiselect because of duplicate value. Added unqiue values only!');
      // Build array with duplicate values.
      let duplicatesToConsole = [];
      for (let i = 0; i < duplicatesCheck.length; i++) {
        const itemCountLength = duplicatesCheck.filter(function (option) {
          return option === duplicatesCheck[i];
        }).length;
        if (itemCountLength > 1) {
          duplicatesToConsole.push(duplicatesCheck[i]);
        }
      }

      duplicatesToConsole = duplicatesToConsole.filter(function (item, index, self) {
        return self.indexOf(item) === index;
      });

      // Delete duplicates from newOptions array
      newOptions = newOptions.filter(function (newOption) {
        return duplicatesToConsole.indexOf(newOption.value) === -1;
      });

      console.warn('Following values was not added: ' + duplicatesToConsole.join(', '));
    }

    // Render multiselect options
    const multiselectOptions = Mustache.render(this.optionsPartial, {
      options: newOptions,
    });

    // Render options to select-element
    const originalOptions = Mustache.render(this.originalOptionsTemplate, {
      options: newOptions,
    });

    // If underOptGroup is defined, find optGroup
    if (underOptGroup !== null && typeof underOptGroup === 'string') {
      const optGroupToInsertOriginalSelect = this.element.querySelector('optgroup[label="' + underOptGroup + '"]');
      const optGroupToInsertMultiselect = this.multiselectList.querySelector('.separator[data-separator-label="' + underOptGroup + '"]');
      // Using nextUntil function from ie-polyfills
      const multiselectOptGroupLastElement = nextUntil(optGroupToInsertMultiselect, '.separator');
      // Both element found, add options to 'em
      if (optGroupToInsertMultiselect && optGroupToInsertOriginalSelect && multiselectOptGroupLastElement[0]) {
        multiselectOptGroupLastElement[0].insertAdjacentHTML('afterend', multiselectOptions);
        optGroupToInsertOriginalSelect.insertAdjacentHTML('beforeend', originalOptions);
      } else {
        console.error('Could not find optgroup! Exiting!');
        return;
      }
    } else {
      this.element.insertAdjacentHTML('beforeend', originalOptions);
      this.multiselectList.insertAdjacentHTML('beforeend', multiselectOptions);
    }

    // Add click events to options
    this.options = this._optionsToJSON();
    this._bindOptionClicks(newOptions);

    this._setValue();

    // Update caption in case of newOptions contains options selected
    this._updateSelectButtonCaption();

    // If any options selected, show clearAll button
    if (this.anyOptionsSelected()) {
      this._showClearAll();
    }
  }

  /**
   * Removes options from multiselect and original select
   *
   * @param {array}   optionsToRemove - Removes options from original select and new select
   * @param {boolean} runCallBack     - If true, method will run onValueChange callback
   *
   * @returns {undefined}
   *
   * @example
   * constructedMultiselect.removeOptions([
   *   'value1', 'value2'
   * ])
   */
  removeOptions(optionsToRemove, runCallBack = true) {
    if (typeof optionsToRemove !== 'object' || !Array.isArray(optionsToRemove)) {
      console.error('optionsToRemove was not array!');
      return;
    }

    // If there is options that are selected, this flag will be
    // true and onValueChange will fire if function is defined
    let valueChanged = false;

    // Iterate options to be removed
    for (const optionValue of optionsToRemove) {
      const optionToBeDeleted = this.options.findIndex(function (option) {
        // Check if option is selected
        if (!valueChanged && option.selected && option.value === optionValue) {
          valueChanged = true;
        }
        return option.value === optionValue;
      });

      if (optionToBeDeleted !== -1) {
        // Delete select and multiselect elements
        const originalSelectOption = this.element.querySelector('[value="' + this.options[optionToBeDeleted].value + '"]'),
          multiselectOption = this.multiselectList.querySelector('[data-value="' + this.options[optionToBeDeleted].value + '"]');

        // Remove DOM-elements
        this.element.removeChild(originalSelectOption);
        this.multiselectList.removeChild(multiselectOption);

        // Delete value from options array
        delete this.options[optionToBeDeleted];
      } else {
        console.warn('Could not find select option with value ' + optionValue);
      }

      if (runCallBack && valueChanged && typeof this.callbacks.onValueChange === 'function') {
        // Fire onValueChange function
        this.callbacks.onValueChange(this);
      }
      // Delete empty array elements
      this.options = this.options.filter(Boolean);
    }

    this._setValue();
    this._updateSelectButtonCaption();
  }

  /**
   * Select option values
   *
   * @param {optionValues} optionValues - Option values to select in array
   *
   * @example
   * window.multiselects.makes.selectOption([
   *  'Audi', 'BMW'
   * ]);
   */
  selectOptions(optionValues) {
    if (typeof optionValues !== 'object' && !Array.isArray(optionValues)) {
      console.error('optionValues is not an array!');
      return;
    }

    for (let i = 0; i < optionValues.length; i++) {
      if (this.element.querySelector('[value="' + optionValues[i] + '"]')) {
        const originalSelectOption = this.element.querySelector('[value="' + optionValues[i] + '"]'),
          multiselectOption = this.multiselectList.querySelector('[data-value="' + optionValues[i] + '"]');

        if (!originalSelectOption.selected) {
          // Emulate option click
          multiselectOption.click();
        }
      }
    }
  }

  /**
   * Unselects all options from the multiselect
   *
   * @param {boolean} runCallBack - If false, callback will not run. Needed for example in single select list option click
   */
  unSelectAll(runCallBack = true) {
    const self = this;
    this.options.map(function (option) {
      if (option.selected) {
        const originalSelectOption = self.element.querySelector('[value="' + option.value + '"]');
        const multiselectOption = self.multiselectList.querySelector('[data-value="' + option.value + '"]');

        multiselectOption.classList.remove('selected');
        originalSelectOption.selected = false;
        originalSelectOption.removeAttribute('selected');

        option.selected = false;
      }
    });

    this.element.selectedIndex = -1;
    this._setValue();
    this._hideClearAll();
    // Run onValueChange if exists
    if (typeof this.callbacks.onValueChange === 'function' && runCallBack) {
      this.callbacks.onValueChange(this, true);
    }

    this._updateSelectButtonCaption();
  }

  /**
   * Opens multiselect UI and close existing multiselect if document has opened
   *
   * @returns {undefined}
   */
  openSelect() {
    // If there is already active multiselect in dom, close it
    if (window.activeMultiselectId !== null) {
      window.multiselects[window.activeMultiselectId].closeSelect();
    }
    window.activeMultiselectId = this.element.id;
    this.selectWrapper.classList.add('in');

    // If menuOpen callback is defined
    if (typeof this.callbacks.onMenuOpen === 'function') {
      this.callbacks.onMenuOpen(this);
    }
  }

  /**
   * Closes multiselect UI
   *
   * @returns {undefined}
   */
  closeSelect() {
    window.activeMultiselectId = null;
    this.selectWrapper.classList.remove('in');

    if (typeof this.callbacks.onMenuClose === 'function') {
      this.callbacks.onMenuClose(this);
    }
  }

  /**
   * Returns true if one or more options are selected
   *
   * @returns {boolean}
   */
  anyOptionsSelected() {
    return this.options.some(function (option) {
      return option.selected === true;
    });
  }

  /**
   * Return selected values in array
   *
   * @returns {array} - values in array list
   */
  getValue() {
    return this.value;
  }

  /**
   * If needed only latest selection details (value and wasSelected boolean), this method returns it
   *
   * @returns {object}
   */
  getLatestSelection() {
    return this.selectDetails.latestSelection;
  }

  /**
   * Disable multiselect element
   *
   * @returns {undefined}
   */
  disableMultiselect() {
    if (!this.multiselectButton.classList.contains('disabled')) {
      this.multiselectButton.classList.add('disabled');
      this.selectDetails.isDisabled = true;
      this.element.disabled = true;
      this._hideClearAll();
      if (window.activeMultiselectId === this.element.id) {
        this.closeSelect();
      }
    }
  }

  /**
   * Enable multiselect element
   *
   * @returns {undefined}
   */
  enableMultiselect() {
    if (this.multiselectButton.classList.contains('disabled')) {
      this.multiselectButton.classList.remove('disabled');
      this.selectDetails.isDisabled = false;
      this.element.disabled = false;
      if (this.anyOptionsSelected()) {
        this._showClearAll();
      }
    }
  }

  /* * * * * * * * * * *
   * Private functions *
   * * * * * * * * * * */

  /**
   * Set value to this.value
   *
   * @return {undefined}
   */
  _setValue() {
    const selectedValues = [];
    for (const option of this.options) {
      if (option.selected) {
        selectedValues.push(option.value);
      }
    }

    this.value = selectedValues;
  }

  /**
   * Updates clear all component content
   *
   * @returns {undefined}
   * @private
   */
  _updateClearAll() {
    const self = this;
    let selectedCount = 0;
    for (const elem of self.options) {
      selectedCount += elem.selected && !elem.hidden ? 1 : 0;
    }
    self.clearAllButtonCount.innerHTML = `(${selectedCount} ${window.TRANSLATIONS.remultiselect_pcs})`;
    self.clearAllButtonText.innerHTML = window.TRANSLATIONS.remultiselect_removeAll;
  }

  /**
   * Shows clear all component
   *
   * @returns {undefined}
   * @private
   */
  _showClearAll() {
    const self = this;
    if (typeof this.element.dataset.noClear === 'undefined') {
      self.clearAllButton.style.display = 'block';
    }
  }

  /**
   * Hides clear all component
   *
   * @returns {undefined}
   * @private
   */
  _hideClearAll() {
    this.clearAllButton.style.display = 'none';
  }

  /**
   * Get options from select and return option values and shown values in object
   *
   * @returns {object}
   * @private
   */
  _optionsToJSON() {
    const optGroups = this.element.querySelectorAll('optgroup'),
      optionsArray = [];

    if (optGroups.length === 0) {
      const optionElements = this.element.querySelectorAll('option');

      for (const optionElement of optionElements) {
        // Assign datasets from DOMStringMap to object
        let datasets = [];

        for (const [key, value] of Object.entries(Object.assign({}, optionElement.dataset))) {
          datasets.push({ datasetName: key, datasetValue: value });
        }

        optionsArray.push({
          value: optionElement.value,
          shownValue: optionElement.innerText,
          selected: optionElement.selected,
          hidden: optionElement.hidden,
          datasets: datasets,
        });
      }
    } else {
      for (const optGroup of optGroups) {
        // Push optGroup option to optionsArray
        optionsArray.push({
          value: optGroup.label,
          shownValue: optGroup.label,
          selected: false,
          isCaption: true,
        });

        // Get optGroup options and push 'em next to optGroup caption
        const optionElements = optGroup.querySelectorAll('option');
        for (const optionElement of optionElements) {
          // Assign datasets from DOMStringMap to object
          let datasets = [];

          for (const [key, value] of Object.entries(Object.assign({}, optionElement.dataset))) {
            datasets.push({ datasetName: key, datasetValue: value });
          }

          optionsArray.push({
            value: optionElement.value,
            shownValue: optionElement.innerText,
            selected: optionElement.selected,
            isCaption: false,
            hidden: optionElement.hidden,
            datasets: datasets,
          });
        }
      }
    }
    return optionsArray;
  }

  /**
   * Binds multiselect basic ui events
   *
   * @returns {undefined}
   * @private
   */
  _bindEvents() {
    const self = this;

    // Toggle menu on button click
    this.multiselectButton.addEventListener('click', function () {
      // If multiselect is disabled, do nothing
      if (self.selectDetails.isDisabled) {
        return;
      }
      // If multiselect is disabled
      if (!self.selectWrapper.classList.contains('in')) {
        self.openSelect();
      } else {
        self.closeSelect();
      }
    });

    // Clear selection event listener
    this.clearAllButton.addEventListener('click', function () {
      if (window.activeMultiselectId === self.element.id) {
        self.closeSelect();
      }
      self.unSelectAll();
    });

    // Window body click event to close active multiselect
    if (typeof window.multiselectBodyClickEventDefined === 'undefined') {
      document.documentElement.addEventListener('click', function (event) {
        if (window.activeMultiselectId !== null && !event.target.closest('.select-container.in')) {
          window.multiselects[window.activeMultiselectId].closeSelect();
        }
        return;
      });
      window.multiselectBodyClickEventDefined = true;
    }

    // Bind option clicks
    this._bindOptionClicks(this.options);
  }

  /**
   * Creates list items click events
   *
   * @param {object} selectOptions - Options in JSON
   *
   * @returns {undefined}
   */
  _bindOptionClicks(selectOptions) {
    const self = this;

    // Loop selectOptions
    for (const selectOption of selectOptions) {
      const listItem = this.multiselectList.querySelector('li[data-value="' + selectOption.value + '"]');

      // If select option is optgroup caption, continue loop
      if (selectOption.isCaption) {
        continue;
      }

      if (listItem) {
        listItem.addEventListener('click', function (event) {
          // Get original select option
          const originalSelectOption = self.element.querySelector('option[value="' + selectOption.value + '"]'),
            isSelected = this.classList.contains('selected');

          // If select list is not multiple, unselect all
          if (!self.selectDetails.isMultiple) {
            // If user is clicking currently selected value, return
            const currentValue = self.getValue();
            if (currentValue.length === 1 && currentValue[0].toUpperCase() === event.target.dataset.value.toUpperCase()) {
              self.closeSelect();
              return;
            }
            // Remove all selection without onValueChange callback
            self.unSelectAll(false);

            // If user selected option without value, return
            if (event.target.dataset.value === '') {
              self.closeSelect();
              if (typeof self.callbacks.onValueChange === 'function') {
                self.callbacks.onValueChange(self, true);
              }
              return;
            }
          }
          // Set latest value to selectDetails
          self.selectDetails.latestSelection.value = event.target.dataset.value;

          // Selecting option
          if (isSelected) {
            this.classList.remove('selected');
            originalSelectOption.selected = false;
            originalSelectOption.removeAttribute('selected');
            self.selectDetails.latestSelection.wasUnChecked = true;
          } else {
            this.classList.add('selected');
            originalSelectOption.selected = true;
            originalSelectOption.setAttribute('selected', '');
            self.selectDetails.latestSelection.wasUnChecked = false;
          }

          // Update options
          self.options = self._optionsToJSON();

          // If select is single-select, hide after option click
          if (!self.selectDetails.isMultiple) {
            self.closeSelect();
          }

          // Check if any options selected and hide or update+show 'clear all' component
          if (self.anyOptionsSelected()) {
            self._updateClearAll();
            self._showClearAll();
          } else {
            self._hideClearAll();
          }

          // Update this.value and button captions
          self._setValue();
          self._updateSelectButtonCaption();

          // Execute onValueChange function if exists
          if (typeof self.callbacks.onValueChange === 'function') {
            self.callbacks.onValueChange(self);
          }
        });
      } else {
        console.warn('Could not find list items with data-value "' + selectOption.value + '" in list "' + this.multiselectList.id + '".');
      }
    }
  }

  /**
   * Renders multiselect template and place it to DOM
   *
   * @returns {undefined}
   * @private
   */
  _renderUI() {
    const objectToMustache = {
      selectDetails: this.selectDetails,
      options: this.options,
      showClearAll: typeof this.element.dataset.noClear === 'undefined' && this.anyOptionsSelected() ? true : false,
    };

    // Create wrapper div
    const wrapper = document.createElement('div');
    wrapper.className = 'select-container';

    // Append wrapper before old select
    this.element.parentNode.insertBefore(wrapper, this.element.nextSibling);

    // Delete original select
    this.element.parentNode.removeChild(this.element);

    // Append original select and remultiselect ui to wrapper
    wrapper.appendChild(this.element);

    // Append mustache rendered template to wrapper
    wrapper.innerHTML += Mustache.render(this.template, objectToMustache, {
      optionsList: this.optionsPartial,
    });

    this.selectWrapper = wrapper;

    // Get appended element to this.element
    this.element = this.selectWrapper.querySelector('select.remultiselect');

    // Save multiselect button and list container for later use
    this.multiselectButton = this.selectWrapper.querySelector('.multiselect-open');
    this.multiselectButtonCaption = this.selectWrapper.querySelector('.multiselect-open__caption');
    this.multiselectListContainer = this.selectWrapper.querySelector('.list-container');
    this.multiselectList = this.multiselectListContainer.querySelector('ul');
    this.clearAllButton = this.selectWrapper.querySelector('.clear-selection');
    this.clearAllButtonText = this.selectWrapper.querySelector('.clear-selection__text');
    this.clearAllButtonCount = this.selectWrapper.querySelector('.clear-selection__count');

    // Bind events
    this._bindEvents();
  }

  /**
   * Updates select button caption
   *
   * @returns {undefined}
   * @private
   */
  _updateSelectButtonCaption() {
    const currentValue = this.getValue();
    // If multiple options selected, show count of selected values
    if (currentValue.length > 1) {
      let outputText = [];
      for (const option of this.options) {
        if (option.selected) {
          outputText.push(option.shownValue);
        }
      }
      this.multiselectButtonCaption.innerText = outputText.join(', ');
      if (!this.multiselectButton.classList.contains('value-selected')) {
        this.multiselectButton.classList.add('value-selected');
      }
    } else if (currentValue.length === 1) {
      // Only one select option selected, show selected value
      // Get shown value from this.options
      const shownValue = this.options.find(function (option) {
        return option.value === currentValue[0];
      });
      if (!shownValue) {
        this.multiselectButtonCaption.innerText = this.element.dataset.caption
          ? this.element.dataset.caption
          : window.TRANSLATIONS.remultiselect_select;
      } else {
        this.multiselectButtonCaption.innerText = shownValue.shownValue;
        if (!this.multiselectButton.classList.contains('value-selected')) {
          this.multiselectButton.classList.add('value-selected');
        }
      }
    } else {
      // Show default caption
      this.multiselectButtonCaption.innerText = this.element.dataset.caption
        ? this.element.dataset.caption
        : window.TRANSLATIONS.remultiselect_select;
      if (this.multiselectButton.classList.contains('value-selected')) {
        this.multiselectButton.classList.remove('value-selected');
        this._hideClearAll();
      }
    }
  }

  /**
   * Collects select details to object which can be used in mustache render
   *
   * @returns {object}
   * @private
   */
  _collectSelectDetails() {
    // Assign datasets from DOMStringMap to object
    let datasets = [];
    for (const [key, value] of Object.entries(Object.assign({}, this.element.dataset))) {
      datasets.push({ datasetName: key, datasetValue: value });
    }

    const selectDetails = {
      name: this.element.name,
      form: this.element.form,
      isDisabled: this.element.disabled,
      isMultiple: this.element.type === 'select-multiple', // If select is multiple
      datasets: datasets,
      defaultCaption: window.TRANSLATIONS.remultiselect_select,
      latestSelection: {
        wasUnChecked: false, // Defines if last selection was unselect
        value: null, // Last selected / unselected value
      },
    };
    return selectDetails;
  }
}

// Build multiselects
const multiselectHtmlElements = document.getElementsByClassName('remultiselect');
for (let i = 0; i < multiselectHtmlElements.length; i++) {
  let htmlElement = multiselectHtmlElements[i];
  if (!multiselects[htmlElement.id]) {
    multiselects[htmlElement.id] = new ReMultiselect(htmlElement);
  }
}
